import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navigation from "@/components/shared/Navigation";
import Footer from "@/components/shared/Footer";
import { ARCHIVE_VERSIONS } from "./privacyArchives";

const PrivacyPolicyArchiveDetail = () => {
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState("");
  const navigate = useNavigate();

  const version = ARCHIVE_VERSIONS.find((v) => v.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [slug]);

  const scrollToSection = (sectionId) => {
    navigate(`/?section=${sectionId}`);
  };

  if (!version) {
    return (
      <div>
        <Navigation
          onSectionClick={scrollToSection}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <div className="max-w-4xl mx-auto px-8 text-center" style={{ paddingTop: "calc(96px + 65px)", paddingBottom: "96px" }}>
          <h1 className="text-2xl font-semibold mb-3" style={{ color: "#0D3D2E" }}>
            Version not found
          </h1>
          <p className="text-[#6B7C70] text-sm mb-6">
            This archived version does not exist.
          </p>
          <Link
            to="/privacy-policy/archive"
            className="text-sm font-medium hover:underline"
            style={{ color: "#1A6645" }}
          >
            ← Back to archive
          </Link>
        </div>
        <Footer onSectionClick={scrollToSection} setActiveTab={setActiveTab} />
      </div>
    );
  }

  return (
    <div>
      <Navigation
        onSectionClick={scrollToSection}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* Archived version banner — paddingTop accounts for fixed navbar */}
      <div
        className="px-8 py-3"
        style={{ background: "#FFF8E8", borderBottom: "1px solid #E8B84B", paddingTop: "calc(12px + 65px)" }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <p
            className="text-sm"
            style={{ color: "#5C4A00", fontFamily: "system-ui, sans-serif" }}
          >
            <strong>Archived version</strong> — This policy was superseded on{" "}
            {version.effectiveTo}.
          </p>
          <Link
            to="/privacy-policy"
            className="text-sm font-semibold hover:underline"
            style={{ color: "#5C4A00", textDecorationColor: "#E8B84B" }}
          >
            View current Privacy Policy →
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#EDF3F4] py-16 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <div
            className="flex items-center gap-2 mb-6 text-sm"
            style={{ fontFamily: "system-ui, sans-serif" }}
          >
            <Link to="/privacy-policy" className="hover:underline" style={{ color: "#006B5A" }}>
              Privacy Policy
            </Link>
            <span className="text-[#9CA3AF]">/</span>
            <Link
              to="/privacy-policy/archive"
              className="hover:underline"
              style={{ color: "#006B5A" }}
            >
              Archive
            </Link>
            <span className="text-[#9CA3AF]">/</span>
            <span className="text-[#5E6574]">{version.label}</span>
          </div>

          <h1 className="text-[32px] font-bold text-[#0F172A] mb-2">
            {version.label} Privacy Policy
          </h1>
          <p className="text-[#5E6574] text-sm" style={{ fontFamily: "system-ui, sans-serif" }}>
            Superseded on {version.effectiveTo} &nbsp;·&nbsp; No longer in effect
          </p>
        </div>
      </section>

      {/* Content — same rendering as the old policy format */}
      <section className="bg-[#EDF3F4] px-6 lg:px-12 pb-20">
        <div className="max-w-5xl mx-auto space-y-10">
          {version.content.map((block, i) => (
            <div key={i}>
              <h2 className="text-[18px] font-semibold text-[#006B5A] mb-2">
                {block.title}
              </h2>
              <div className="space-y-4 text-[#5E6574]">
                {block.sections.map((sec, idx) => {
                  if (sec.type === "paragraph") {
                    return <p key={idx}>{sec.content}</p>;
                  }
                  if (sec.type === "list") {
                    return (
                      <ul key={idx} className="list-disc pl-5 space-y-2">
                        {sec.items.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom nav */}
        <div
          className="max-w-5xl mx-auto mt-16 pt-8 flex items-center justify-between flex-wrap gap-4"
          style={{ borderTop: "1px solid #C8DECE", fontFamily: "system-ui, sans-serif" }}
        >
          <Link
            to="/privacy-policy/archive"
            className="text-sm hover:underline"
            style={{ color: "#1A6645" }}
          >
            ← Back to archive
          </Link>
          <Link
            to="/privacy-policy"
            className="text-sm font-medium hover:underline"
            style={{ color: "#1A6645" }}
          >
            View current Privacy Policy →
          </Link>
        </div>
      </section>

      <Footer onSectionClick={scrollToSection} setActiveTab={setActiveTab} />
    </div>
  );
};

export default PrivacyPolicyArchiveDetail;
